/**
 * BFP-1025 — local signup-survey client flags (mirrors mobile Hive prefs).
 * Scoped per Signal Provider user id.
 */

const PREFIX = 'brushfire_signup_survey'

function storageKey(userId: string, suffix: string): string {
  return `${PREFIX}:${userId}:${suffix}`
}

function readRaw(key: string): string | null {
  if (globalThis.localStorage === undefined) return null
  try {
    return globalThis.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeRaw(key: string, value: string): void {
  if (globalThis.localStorage === undefined) return
  try {
    globalThis.localStorage.setItem(key, value)
  } catch {
    /* private mode / quota */
  }
}

function removeRaw(key: string): void {
  if (globalThis.localStorage === undefined) return
  try {
    globalThis.localStorage.removeItem(key)
  } catch {
    /* private mode / quota */
  }
}

export type CachedSignupSurveyCard = {
  instrumentId: string
  campaignId: string
  name: string
  description: string | null
  estimatedCompletionMinutes: number
  questionCount: number
  reward: {
    enabled: boolean
    amount: number | null
    display: string | null
  }
  availableSlots: number
  instrumentStatus: 'fresh' | 'resume'
  lastAnsweredQuestionId: string | null
  hasActiveSession: boolean
  activeResponseId: string | null
}

export function isEligibilityChecked(userId: string): boolean {
  return readRaw(storageKey(userId, 'eligibility_checked')) === '1'
}

export function markEligibilityChecked(userId: string): void {
  writeRaw(storageKey(userId, 'eligibility_checked'), '1')
}

export function getCachedSignupSurveyCard(userId: string): CachedSignupSurveyCard | null {
  const raw = readRaw(storageKey(userId, 'cached_card'))
  if (!raw) return null
  try {
    return JSON.parse(raw) as CachedSignupSurveyCard
  } catch {
    return null
  }
}

export function setCachedSignupSurveyCard(
  userId: string,
  card: CachedSignupSurveyCard | null,
): void {
  const cardKey = storageKey(userId, 'cached_card')
  if (!card) {
    removeRaw(cardKey)
    removeRaw(storageKey(userId, 'instrument_id'))
    return
  }
  writeRaw(cardKey, JSON.stringify(card))
  writeRaw(storageKey(userId, 'instrument_id'), card.instrumentId)
}

export function getSignupSurveyInstrumentId(userId: string): string | null {
  return readRaw(storageKey(userId, 'instrument_id'))
}

export function isSignupSurveySubmitted(userId: string): boolean {
  return readRaw(storageKey(userId, 'submitted')) === '1'
}

export function markSignupSurveySubmitted(userId: string): void {
  writeRaw(storageKey(userId, 'submitted'), '1')
  // Drop Home card cache only — keep instrument_id so completion CTAs
  // still detect signup-survey mode (Start eKYC / Remind Me Later).
  removeRaw(storageKey(userId, 'cached_card'))
}

export function isEkycRemindPending(userId: string): boolean {
  return readRaw(storageKey(userId, 'ekyc_remind_pending')) === '1'
}

export function setEkycRemindPending(
  userId: string,
  pending: boolean,
  rewardDisplay?: string | null,
): void {
  if (!pending) {
    removeRaw(storageKey(userId, 'ekyc_remind_pending'))
    removeRaw(storageKey(userId, 'pending_reward_display'))
    return
  }
  writeRaw(storageKey(userId, 'ekyc_remind_pending'), '1')
  if (rewardDisplay) {
    writeRaw(storageKey(userId, 'pending_reward_display'), rewardDisplay)
  }
}

export function getPendingRewardDisplay(userId: string): string | null {
  return readRaw(storageKey(userId, 'pending_reward_display'))
}

export function clearOnTerminalEkycOutcome(userId: string): void {
  setEkycRemindPending(userId, false)
}

/** Defer Home/Rewards eKYC while signup survey is still owed (not yet submitted). */
export function shouldDeferEkycForPendingSignupSurvey(userId: string): boolean {
  if (isSignupSurveySubmitted(userId)) return false
  if (!isEligibilityChecked(userId)) return true
  if (getCachedSignupSurveyCard(userId) != null) return true
  // Instrument remembered but not submitted → still owed (cache may have been cleared in a race).
  return getSignupSurveyInstrumentId(userId) != null
}

/**
 * Suppress Home auto-prompt for signup-survey audience.
 * After Remind Me Later, nag is Rewards-only (TC-13).
 */
export function shouldSuppressHomeEkycAutoPrompt(userId: string): boolean {
  if (shouldDeferEkycForPendingSignupSurvey(userId)) return true
  // After Remind Me Later, nag is Rewards-only — still suppress Home (TC-13).
  if (isEkycRemindPending(userId)) return true
  return false
}

export function isSignupSurveyInstrument(userId: string, instrumentId: string): boolean {
  const stored = getSignupSurveyInstrumentId(userId)
  return Boolean(stored && stored === instrumentId)
}
